import type { GenericFormType } from '@neo/blocks/forms/types'
import { useEffect, useState } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import type { McpArticle } from 'v2/pages/flow/knowledge-base/types'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type { NewKBFormValues } from '../../types'
import {
  AppConnectionBanner,
  AppDocumentTree,
  AppSourceSelector,
  DocumentPreviewSheet,
} from './components'
import type { TreeViewItem } from './document-tree-view'
import { useAppSourceData, useDocumentTree } from './hooks'

export const AppSource = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewDocument, setPreviewDocument] = useState<McpArticle | null>(null)
  const [previewDocumentId, setPreviewDocumentId] = useState<string>('')

  // Use custom hooks for data and state management
  const {
    availableKnowledgeBaseSources,
    selectedAppProvider,
    selectedAppSourceId,
    urlSelectionType,
    isConnected,
    getAppLogo,
    getAppSourceOptionById,
    isAppLoading,
    finalDocumentsData,
    defaultCheckedIds,
    previousAppDataRef,
    isInitialLoadRef,
    loadCollectionArticles,
    loadingCollectionIds,
    collectionHasMore,
  } = useAppSourceData(form)

  const {
    treeData,
    totalPageCount,
    collectionCount,
    loosePageCount,
    selectedCount,
    handleCheckChange,
    handleNewSelectionChange,
    handleTreeAction,
  } = useDocumentTree({
    finalDocumentsData: finalDocumentsData || [],
    defaultCheckedIds,
    urlSelectionType,
    selectedAppProvider,
    form,
    isConnected,
    previousAppDataRef,
    isInitialLoadRef,
    setPreviewDocument,
    setPreviewDocumentId,
    setIsPreviewOpen,
    loadingCollectionIds,
    collectionHasMore,
  })

  const handleAppChange = (appId: string) => {
    const selectedAppOption = getAppSourceOptionById(appId)
    if (selectedAppOption) {
      queryClient.removeQueries({
        queryKey: flowQueryKeys().documentsFromSourceBase,
      })
      form.setFieldValue('app_source', selectedAppOption.provider)
      form.setFieldValue('app_source_id', selectedAppOption.id)
    }
  }

  // Update form loading state when app loading state changes
  useEffect(() => {
    const loadingState = Boolean(isAppLoading)
    form.setFieldValue('appLoading', loadingState)
  }, [isAppLoading, form])

  // Lazy picker: when a space (collection) is expanded, fetch its pages on demand. The hook dedups,
  // so re-expanding an already-loaded space is a no-op.
  const handleExpand = (item: TreeViewItem) => {
    if (item.type === 'collection') loadCollectionArticles(item.id)
  }

  return (
    <div className="flex flex-col gap-2">
      <AppSourceSelector
        selectedAppProvider={selectedAppProvider}
        selectedAppSourceId={selectedAppSourceId}
        availableKnowledgeBaseSources={availableKnowledgeBaseSources}
        onAppChange={handleAppChange}
      />

      <AppConnectionBanner
        selectedAppProvider={selectedAppProvider}
        isConnected={selectedAppProvider ? isConnected(selectedAppProvider) : false}
        getAppLogo={getAppLogo}
        urlSelectionType={urlSelectionType}
      />

      <AppDocumentTree
        selectedAppProvider={selectedAppProvider}
        isConnected={selectedAppProvider ? isConnected(selectedAppProvider) : false}
        urlSelectionType={urlSelectionType}
        getAppLogo={getAppLogo}
        isAppLoading={isAppLoading || false}
        totalPageCount={totalPageCount}
        collectionCount={collectionCount}
        loosePageCount={loosePageCount}
        selectedCount={selectedCount}
        treeData={treeData}
        defaultCheckedIds={defaultCheckedIds}
        onCheckChange={handleCheckChange}
        onNewSelectionChange={handleNewSelectionChange}
        onTreeAction={handleTreeAction}
        onExpand={handleExpand}
      />

      <DocumentPreviewSheet
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        document={previewDocument}
        source={selectedAppProvider}
        sourceId={selectedAppSourceId}
        documentId={previewDocumentId}
      />
    </div>
  )
}
